import React, { useState, useMemo, useCallback, useEffect, useContext, useRef } from 'react';
import { FaSyncAlt, FaTable, FaThLarge, FaVideo, FaFilter } from 'react-icons/fa';
import { useImmer } from 'use-immer';
import { useSelector } from 'react-redux';
import './cctv-dashboard.scss';

// MUI Components
import {
  Autocomplete,
  TextField,
  Chip,
  Button,
  Typography,
} from '@mui/material';

// Components
import CCTVStatCards from './components/CCTVStatCards';
import CCTVCharts from './components/CCTVCharts';
import CCTVCameraTable from './components/CCTVCameraTable';
import CCTVCameraCards from './components/CCTVCameraCards';
import CCTVCameraModal from './components/CCTVCameraModal';
import { AutocompleteStationListPop } from '../AutocompleteStationListPop/AutocompleteStationListPop';

// Types and Constants
import { CCTVCamera, ViewMode } from './utils/cctvTypes';
import { POLLING_INTERVAL } from './utils/cctvConstants';

// API Service
import {
  fetchCCTVCameras,
  getCCTVSystemId,
  calculateCCTVStats,
} from './utils/cctvApiService';

// WebSocket Hook for live updates
import { useCCTVWebSocket } from './utils/useCCTVWebSocket';

// Context and Utils
import { ActiveAlarmsData } from '../Alarms/ActiveAlarms';
import { toast } from 'react-hot-toast';
import { getParsedLocalStorage } from '../../Utils/storage-utils';
import { extractParsedDataFromPermissions } from '../../Utils/Utils';
import { selectedCustomersSelector } from '../../features/authentication/auth-slice';

// Filter interface
interface FilterState {
  zone_ids: any[];
  div_ids: any[];
  customer_ids: any[];
}

const initialFilters: FilterState = {
  zone_ids: [],
  div_ids: [],
  customer_ids: [],
};

const CCTVDashboard: React.FC = () => {
  console.log('=== CCTV Dashboard Rendering ===');

  // State
  const [activeView, setActiveView] = useState<ViewMode>('table');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState<CCTVCamera | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [cameras, setCameras] = useState<CCTVCamera[]>([]);

  // Filter state
  const [filters, setFilters] = useImmer<FilterState>(initialFilters);

  // Refs
  const apiControllerRef = useRef<AbortController | null>(null);

  // Context and Redux
  const { customerIdList }: any = useContext(ActiveAlarmsData);
  const selectedCustomersList = useSelector(selectedCustomersSelector);

  // Parse permissions for Zone/Division/Station dropdowns
  const user_permissions = getParsedLocalStorage('permissions', null);
  const parsedPermissions = useMemo(() => {
    return extractParsedDataFromPermissions(user_permissions);
  }, [user_permissions]);

  // Permitted customer IDs for filtering
  const permittedCustomerIds = useMemo(() => {
    return selectedCustomersList?.map((c: any) => ({ id: c.customerId, value: c.customerName })) || [];
  }, [selectedCustomersList]);

  // Get CCTV system ID from systems list
  const cctvSystemId = useMemo(() => {
    const systemId = getCCTVSystemId();
    console.log('CCTV Dashboard - systemId:', systemId, 'customerIdList:', customerIdList);
    return systemId;
  }, [customerIdList]);

  // Zone dropdown options
  const zoneOptions = useMemo(() => {
    return parsedPermissions.zones || [];
  }, [parsedPermissions.zones]);

  // Division dropdown options - filtered by selected zones
  const divisionOptions = useMemo(() => {
    let totalDivs = parsedPermissions.divisions || [];
    if (filters.zone_ids.length > 0) {
      const selectedZoneIds = filters.zone_ids.map((z: any) => z.id);
      totalDivs = totalDivs.filter((div: any) => selectedZoneIds.includes(div.zone?.id));
    }
    return totalDivs;
  }, [parsedPermissions.divisions, filters.zone_ids]);

  // Station dropdown options - filtered by selected zones and divisions
  const stationOptions = useMemo(() => {
    let filteredStations = (parsedPermissions.stations || []).filter((stn: any) =>
      permittedCustomerIds.find((c: any) => c.id === stn.id)
    );

    if (filters.div_ids.length > 0) {
      const selectedDivIds = filters.div_ids.map((d: any) => d.id);
      filteredStations = filteredStations.filter((stn: any) => selectedDivIds.includes(stn.division?.id));
    } else if (filters.zone_ids.length > 0) {
      const selectedZoneIds = filters.zone_ids.map((z: any) => z.id);
      filteredStations = filteredStations.filter((stn: any) => selectedZoneIds.includes(stn.zone?.id));
    }

    return filteredStations;
  }, [parsedPermissions.stations, permittedCustomerIds, filters.div_ids, filters.zone_ids]);

  // Get customer IDs for API call based on filters
  const getFilteredCustomerIds = useCallback(() => {
    if (filters.customer_ids.length > 0) {
      return filters.customer_ids.map((c: any) => c.id);
    }
    if (filters.div_ids.length > 0 || filters.zone_ids.length > 0) {
      return stationOptions.map((s: any) => s.id);
    }
    return customerIdList;
  }, [filters.customer_ids, filters.div_ids, filters.zone_ids, stationOptions, customerIdList]);

  // Load telemetry data
  const loadTelemetryData = useCallback(async (showLoading = true) => {
    const filteredCustomerIds = getFilteredCustomerIds();

    if (!cctvSystemId || filteredCustomerIds.length === 0) {
      setError('No CCTV system configured or no stations assigned');
      setIsLoading(false);
      return;
    }

    // Cancel previous request
    if (apiControllerRef.current) {
      apiControllerRef.current.abort();
    }
    apiControllerRef.current = new AbortController();

    if (showLoading) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);

    try {
      const cctvCameras = await fetchCCTVCameras(filteredCustomerIds, cctvSystemId);
      setCameras(cctvCameras);
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('Error loading telemetry:', err);
        setError(err.message || 'Failed to load CCTV data');
        toast.error('Failed to load CCTV camera data');
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [cctvSystemId, getFilteredCustomerIds]);

  // WebSocket for live camera status updates
  const handleWebSocketUpdate = useCallback((updatedCameras: CCTVCamera[]) => {
    setCameras(updatedCameras);
  }, []);

  const { isConnected: isWebSocketConnected } = useCCTVWebSocket({
    cameras,
    enabled: !isLoading && cameras.length > 0,
    onUpdate: handleWebSocketUpdate,
  });

  // Initial data load
  useEffect(() => {
    console.log('CCTV Dashboard useEffect - customerIdList.length:', customerIdList.length, 'cctvSystemId:', cctvSystemId);
    if (customerIdList.length > 0 && cctvSystemId) {
      loadTelemetryData();
    } else if (customerIdList.length > 0 && !cctvSystemId) {
      // No CCTV system found - show error
      setError('No CCTV system found in configuration. Please ensure CCTV system is set up.');
      setIsLoading(false);
    }
  }, [customerIdList, cctvSystemId]);

  // Auto-refresh every 30 seconds (fallback if WebSocket disconnects)
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isLoading && !isRefreshing && !isWebSocketConnected) {
        loadTelemetryData(false);
      }
    }, POLLING_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, [loadTelemetryData, isLoading, isRefreshing, isWebSocketConnected]);

  // Get unique stations from cameras for grouping
  const cameraStations = useMemo(() => {
    const uniqueStations = [...new Set(cameras.map(c => c.station))];
    return uniqueStations.sort();
  }, [cameras]);

  // Calculate stats
  const stats = useMemo(() => calculateCCTVStats(cameras), [cameras]);

  // Filter handlers
  const handleFilterChange = useCallback((value: any, field: keyof FilterState) => {
    setFilters((prev) => {
      (prev as any)[field] = value;
      // Clear dependent filters
      if (field === 'zone_ids') {
        prev.div_ids = [];
        prev.customer_ids = [];
      } else if (field === 'div_ids') {
        prev.customer_ids = [];
      }
    });
  }, [setFilters]);

  const handleApplyFilters = useCallback(() => {
    loadTelemetryData();
  }, [loadTelemetryData]);

  const handleClearFilters = useCallback(() => {
    setFilters(initialFilters);
    setTimeout(() => {
      loadTelemetryData();
    }, 100);
  }, [setFilters, loadTelemetryData]);

  const handleRefresh = useCallback(() => {
    loadTelemetryData(false);
  }, [loadTelemetryData]);

  const handleCameraClick = useCallback((camera: CCTVCamera) => {
    setSelectedCamera(camera);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedCamera(null);
  }, []);

  // Render tag chips for autocomplete
  const renderTags = (value: any[], getTagProps: any) => {
    if (value.length === 0) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <Chip size="small" variant="outlined" label={value[0]?.value} {...getTagProps(0)} />
        {value.length > 1 && (
          <AutocompleteStationListPop title={value.map((opt) => opt?.value ?? '').join(', ')}>
            <Chip size="small" label={`+${value.length - 1}`} />
          </AutocompleteStationListPop>
        )}
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="cctv-dashboard">
        <header className="cctv-header">
          <div className="logo-container">
            <div className="cctv-logo"><FaVideo /></div>
            <div className="cctv-title">
              <h1>CCTV Monitoring Dashboard</h1>
              <div className="cctv-subtitle">Real-time camera status monitoring</div>
            </div>
          </div>
          <div className="system-status">
            <div className="status-indicator">
              <div className="status-dot loading" />
              <span>Loading...</span>
            </div>
          </div>
        </header>
        <div className="loading-container">
          <div className="loading-spinner">
            <FaVideo className="spin" />
          </div>
          <p>Loading CCTV camera data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && cameras.length === 0) {
    return (
      <div className="cctv-dashboard">
        <header className="cctv-header">
          <div className="logo-container">
            <div className="cctv-logo"><FaVideo /></div>
            <div className="cctv-title">
              <h1>CCTV Monitoring Dashboard</h1>
              <div className="cctv-subtitle">Real-time camera status monitoring</div>
            </div>
          </div>
        </header>
        <div className="error-container">
          <div className="error-message">
            <p>{error}</p>
            <button className="retry-btn" onClick={() => loadTelemetryData()}>
              <FaSyncAlt /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cctv-dashboard">
      {/* Header */}
      <header className="cctv-header">
        <div className="logo-container">
          <div className="cctv-logo"><FaVideo /></div>
          <div className="cctv-title">
            <h1>CCTV Monitoring Dashboard</h1>
            <div className="cctv-subtitle">Real-time camera status monitoring</div>
          </div>
        </div>
        <div className="system-status">
          <div className="status-indicator">
            <div className={`status-dot ${isRefreshing ? 'loading' : isWebSocketConnected ? 'online' : 'online'}`} />
            <span>{isRefreshing ? 'Refreshing...' : isWebSocketConnected ? 'Live' : 'Operational'}</span>
          </div>
        </div>
      </header>

      {/* Filter Section */}
      <div className="cctv-filter-section">
        <div className="filter-row">
          {/* Zone Dropdown */}
          <Autocomplete
            size="small"
            className="filter-input"
            multiple
            disableCloseOnSelect
            options={zoneOptions}
            value={filters.zone_ids}
            onChange={(_, newValue) => handleFilterChange(newValue, 'zone_ids')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(opt) => opt.value || ''}
            renderInput={(params) => <TextField {...params} label="Zones" />}
            renderTags={renderTags}
          />

          {/* Division Dropdown */}
          <Autocomplete
            size="small"
            className="filter-input"
            multiple
            disableCloseOnSelect
            options={divisionOptions}
            value={filters.div_ids}
            onChange={(_, newValue) => handleFilterChange(newValue, 'div_ids')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(opt) => opt.value || ''}
            renderInput={(params) => <TextField {...params} label="Divisions" />}
            renderTags={renderTags}
          />

          {/* Station Dropdown */}
          <Autocomplete
            size="small"
            className="filter-input"
            multiple
            disableCloseOnSelect
            options={stationOptions}
            value={filters.customer_ids}
            onChange={(_, newValue) => handleFilterChange(newValue, 'customer_ids')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(opt) => opt.value || ''}
            renderInput={(params) => <TextField {...params} label="Stations" />}
            renderTags={renderTags}
          />

          {/* Filter Buttons */}
          <div className="filter-actions">
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyFilters}
              disabled={isRefreshing}
              startIcon={<FaFilter />}
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              disabled={isRefreshing}
            >
              Clear
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
              startIcon={<FaSyncAlt className={isRefreshing ? 'spin' : ''} />}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Info */}
        <div className="filter-summary">
          <Typography variant="body2" color="textSecondary">
            Showing <strong>{cameras.length}</strong> cameras across <strong>{cameraStations.length}</strong> stations
            {filters.zone_ids.length > 0 && ` in ${filters.zone_ids.length} zone(s)`}
            {filters.div_ids.length > 0 && `, ${filters.div_ids.length} division(s)`}
            {filters.customer_ids.length > 0 && `, ${filters.customer_ids.length} station(s)`}
          </Typography>
        </div>
      </div>

      {/* Summary Stats Cards */}
      <CCTVStatCards stats={stats} />

      {/* Charts Section */}
      <CCTVCharts cameras={cameras} stats={stats} />

      {/* Camera View Section */}
      <div className="tabs-section">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeView === 'table' ? 'active' : ''}`}
            onClick={() => setActiveView('table')}
          >
            <FaTable />
            Table View
          </button>
          <button
            className={`tab-button ${activeView === 'card' ? 'active' : ''}`}
            onClick={() => setActiveView('card')}
          >
            <FaThLarge />
            Card View
          </button>
        </div>

        {/* Table View */}
        <div className={`tab-content ${activeView === 'table' ? 'active' : ''}`}>
          <CCTVCameraTable cameras={cameras} onCameraClick={handleCameraClick} />
        </div>

        {/* Card View */}
        <div className={`tab-content ${activeView === 'card' ? 'active' : ''}`}>
          <CCTVCameraCards cameras={cameras} onCameraClick={handleCameraClick} />
        </div>
      </div>

      {/* Camera Detail Modal */}
      {isModalOpen && selectedCamera && (
        <CCTVCameraModal camera={selectedCamera} onClose={handleCloseModal} />
      )}
    </div>
  );
};

export default CCTVDashboard;
